import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  eventId: string;
  team: {
    team_name: string;
    division: string;
    registration_id?: string | null;
    team_id?: string | null;
  } | null;
  onChanged: () => void;
}

export default function WithdrawTeamDialog({ open, onOpenChange, eventId, team, onChanged }: Props) {
  const [step, setStep] = useState<'confirm' | 'done'>('confirm');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ affected_division: string | null; singleton_pools: string[] } | null>(null);

  useEffect(() => {
    if (open) { setStep('confirm'); setResult(null); }
  }, [open]);

  const doWithdraw = async () => {
    if (!team) return;
    setBusy(true);
    try {
      const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: {
          action: 'withdraw_team',
          event_id: eventId,
          registration_id: team.registration_id ?? null,
          team_id: team.team_id ?? null,
        },
      });
      if (error || !res?.ok) {
        toast.error(error?.message || res?.error?.message || 'Could not withdraw the team.');
        return;
      }
      setResult(res.data);
      setStep('done');
      toast.success(`${team.team_name} withdrawn from play.`);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    const division = result?.affected_division || team?.division;
    if (!division) { onOpenChange(false); return; }
    setBusy(true);
    try {
      const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'regenerate_division', event_id: eventId, division },
      });
      if (error || !res?.ok) {
        toast.error(error?.message || res?.error?.message || 'Could not regenerate the division.');
        return;
      }
      const d = res.data;
      toast.success(`Re-pooled ${division}: ${d.pools} pools, ${d.games} games.${d.singleton_pools?.length ? ' ' + d.singleton_pools.length + ' singleton pool(s).' : ''}`);
      for (const who of d.prevented_pairs_skipped ?? []) {
        toast.info(`No game generated between ${who} — they are a prevented matchup.`);
      }
      for (const line of d.prevented_pairs_unavoidable ?? []) toast.warning(line);
      onOpenChange(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {step === 'confirm' ? (
          <>
            <DialogHeader>
              <DialogTitle>Withdraw team from play?</DialogTitle>
              <DialogDescription>
                <strong>{team?.team_name}</strong> ({team?.division}) will be removed from its pool and
                its games will be cancelled. The team's registration and payment record are kept intact and
                marked as withdrawn — this does not delete the registration.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
              <Button variant="destructive" onClick={doWithdraw} disabled={busy}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Withdraw team
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" /> Team withdrawn
              </DialogTitle>
              <DialogDescription>
                {team?.team_name} has been removed from competition in {result?.affected_division || team?.division}.
              </DialogDescription>
            </DialogHeader>

            {result?.singleton_pools && result.singleton_pools.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
                <div className="flex items-center gap-2 font-medium text-amber-800 mb-1">
                  <AlertTriangle className="h-4 w-4" /> Singleton pools
                </div>
                <p className="text-amber-800">
                  These pools now have only one team (no games):
                </p>
                <ul className="list-disc pl-5 text-amber-800 mt-1">
                  {result.singleton_pools.map((p) => <li key={p}>{p}</li>)}
                </ul>
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              Re-pool and regenerate just this division now? Games will be recreated unscheduled —
              run "Assign times" afterward.
            </p>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Close</Button>
              <Button onClick={regenerate} disabled={busy}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Re-pool {result?.affected_division || team?.division}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
